import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { Destroyable, SaveForm, selectSomeOnce, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { selectAllGuests, selectGuestById } from '@tv3/store/guest/guest.selectors'
import { SearchGuests, UpdateGuest, GetGuest } from '@tv3/store/guest/guest.actions'
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators'
import * as R from 'ramda'
import { BehaviorSubject } from 'rxjs'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { Guest } from '@tv3/store/guest/guest.model'
import { ChangeInquiryGuest } from '@tv3/store/inquiry/inquiry-fields.actions'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { ChannelGuard } from '@tv3/guards/channel.guard'
import { TruncatePipe } from '@tokeet-frontend/tv3-platform'
import * as moment from 'moment'
import { InitialWhatsappDialogService } from '@tv3/containers/messages/initial-whatsapp-dialog/initial-whatsapp-dialog.service'
@Component({
  selector: 'app-edit-guest',
  templateUrl: './edit-guest-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./edit-guest-dialog.component.scss'],
})
export class EditGuestDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    guestId: [''],
  })

  guestForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', [Validators.required]],
    phone: [''],
    whatsapp_phone: [''],
    address: this.fb.group({
      address: [''],
      city: [''],
      state: [''],
      country: [],
      country_code: [],
      postal_code: [''],
    }),
  })

  guests$ = this.store.pipe(
    select(selectAllGuests),
    map((guests) =>
      R.map(
        (g) => ({
          ...g,
          label: `${g.name} - ${new TruncatePipe().transform(g.primaryEmail, 20)}`,
        }),
        guests
      )
    )
  )

  guestSearch = new BehaviorSubject<string>('')
  inquiry: Inquiry
  emails = []
  guest: Guest

  constructor(
    public dialogRef: MatDialogRef<EditGuestDialogComponent>,
    private fb: FormBuilder,
    private dataCheckerService: DataCheckerService,
    private initialWhatsappDialogService: InitialWhatsappDialogService,
    private store: Store<fromRoot.State>,
    @Inject(MAT_DIALOG_DATA) public data: { inquiry: Inquiry }
  ) {
    super()
    this.dataCheckerService.check([ChannelGuard])
  }

  ngOnInit() {
    const inquiry$ = this.store.pipe(select(selectInquiry, { id: this.data.inquiry.id }))
    inquiry$.pipe(untilDestroy(this)).subscribe((inquiry) => {
      this.inquiry = inquiry
    })

    this.form
      .get('guestId')
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap((guestId) => this.store.dispatch(GetGuest({ id: guestId }))),
        switchMap((guestId) => this.store.pipe(selectSomeOnce(selectGuestById, { id: guestId }))),
        untilDestroy(this)
      )
      .subscribe((guest) => {
        this.guest = guest
        this.setGuestForm(guest)
        this.setEmails(guest)
      })

    this.form.patchValue({ guestId: this.data.inquiry.guestId })

    this.store.dispatch(SearchGuests({ term: '' }))

    this.guestSearch
      .pipe(
        filter((name) => !R.isEmpty(name)),
        debounceTime(250),
        untilDestroy(this)
      )
      .subscribe((term) => {
        this.store.dispatch(SearchGuests({ term }))
      })
  }

  setEmails(guest: Guest) {
    this.emails = R.pipe(
      R.keys,
      R.map((e) => {
        let email = decodeURIComponent(e)
        return {
          email,
          primary: email === guest.primaryEmail,
        }
      })
    )(guest.email)
  }

  setGuestForm(guest: Guest) {
    if (!guest) {
      return
    }
    const address = guest.address
    this.guestForm.patchValue({
      name: guest.name,
      email: guest.primaryEmail || '',
      birthday: guest.birthday ? moment(guest.birthday, 'MM/DD/YYYY') : '',
      wife: guest.wife ? guest.wife : '',
      phone: guest.phone,
      whatsapp_phone: guest.whatsapp_phone,
      address: {
        address: address?.address,
        city: address?.city,
        country: address?.country,
        country_code: address?.countryCode,
        state: address?.state,
        postal_code: address?.postalCode,
      },
    })
  }

  onSendWhatsappMessage() {
    this.initialWhatsappDialogService.open({
      guest: { ...this.guest, ...this.guestForm.getRawValue() },
      inquiry: this.inquiry,
    })
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  save(form: FormGroup) {
    if (this.guest && this.inquiry.guestId !== this.guest?.id) {
      this.store.dispatch(ChangeInquiryGuest({ inquiry: this.inquiry, guest: this.guest }))
    }

    const { birthday, ...values } = form.getRawValue()
    this.store.dispatch(
      UpdateGuest({
        guestId: this.form.get('guestId').value,
        inquiry: this.inquiry,
        form: {
          ...values,
          birthday: moment.isMoment(birthday) ? moment(birthday).format('MM/DD/YYYY') : '',
        },
      })
    )
    this.close()
  }
}
